import React, { useEffect, useMemo } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonText,
  IonButtons,
  IonBackButton,
  IonSpinner,
} from '@ionic/react';
import { useAuth } from '@clerk/clerk-react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { useI18n } from '../i18n/I18nProvider';
import { usePageView } from '../hooks/useLoggerInit';
import {
  clearPendingDeepLink,
  destinationForDeepLink,
  savePendingDeepLink,
  type DeepLinkKind,
} from '../lib/deepLinks';

/**
 * Public entry for share links:
 * - /c/:id → chat (after login)
 * - /t/:id → topic conversations (after login)
 */
const DeepLinkLandingPage: React.FC = () => {
  const chatMatch = useRouteMatch<{ id: string }>('/c/:id');
  const topicMatch = useRouteMatch<{ id: string }>('/t/:id');
  const params = useParams<{ id: string }>();
  const kind: DeepLinkKind | null = chatMatch
    ? 'chat'
    : topicMatch
      ? 'topic'
      : null;
  const id = (params.id || '').trim();
  const destination = useMemo(
    () => (kind ? destinationForDeepLink(kind, id) : null),
    [kind, id]
  );

  const { isSignedIn, isLoaded } = useAuth();
  const history = useHistory();
  const { t } = useI18n();
  usePageView(kind === 'topic' ? 'deep_link_topic' : 'deep_link_chat');

  useEffect(() => {
    if (!destination) {
      history.replace('/');
      return;
    }
    if (!isLoaded) return;

    if (isSignedIn) {
      clearPendingDeepLink();
      history.replace(destination);
      return;
    }

    savePendingDeepLink(destination);
  }, [destination, isLoaded, isSignedIn, history]);

  if (!kind || !destination) {
    return null;
  }

  const title =
    kind === 'chat' ? t('deepLink.chat.title') : t('deepLink.topic.title');
  const headline =
    kind === 'chat' ? t('deepLink.chat.headline') : t('deepLink.topic.headline');
  const subhead =
    kind === 'chat' ? t('deepLink.chat.subhead') : t('deepLink.topic.subhead');

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/" text={t('common.actions.back')} />
          </IonButtons>
          <IonTitle>{title}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding vault-screen">
        <div className="deep-link-landing">
          {!isLoaded || isSignedIn ? (
            <div className="ion-text-center ion-padding">
              <IonSpinner name="crescent" />
            </div>
          ) : (
            <>
              <h1>{headline}</h1>
              <p>{subhead}</p>
              <IonText color="medium">
                <p className="deep-link-landing__note">
                  {t('deepLink.loginNote')}
                </p>
              </IonText>
              <div className="deep-link-landing__actions">
                <IonButton
                  expand="block"
                  onClick={() => {
                    savePendingDeepLink(destination);
                    history.push('/sign-in');
                  }}
                >
                  {t('deepLink.signIn')}
                </IonButton>
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={() => {
                    savePendingDeepLink(destination);
                    history.push('/sign-up');
                  }}
                >
                  {t('deepLink.signUp')}
                </IonButton>
              </div>
            </>
          )}
        </div>
        <style>{`
          .deep-link-landing {
            max-width: 420px;
            margin: 2rem auto 0;
            text-align: center;
          }
          .deep-link-landing h1 {
            margin: 0 0 0.5rem;
            font-size: 1.45rem;
          }
          .deep-link-landing p {
            margin: 0 0 0.75rem;
            color: var(--ion-color-medium);
            line-height: 1.45;
          }
          .deep-link-landing__note {
            font-size: 0.85rem;
          }
          .deep-link-landing__actions {
            display: flex;
            flex-direction: column;
            gap: 0.65rem;
            margin-top: 1.5rem;
          }
        `}</style>
      </IonContent>
    </IonPage>
  );
};

export default DeepLinkLandingPage;
